import memoize from 'lodash/memoize';
import { wait } from '~/common/js/utils';


/**
 * Compound Top-Level Domains
 */
const ccTLDs = [
	'com.cn',
  'net.cn',
  'org.cn',
  'gov.cn',
  'edu.cn',
  'co.uk',
  'org.uk',
  'me.uk',
  'gov.uk',
  'edu.au',
  'com.au',
  'org.au',
  'net.au',
  'co.nz',
  'org.nz',
  'net.nz',
  'com.br',
  'net.br',
  'org.br',
  'edu.br',
  'gov.br',
  'co.in',
  'org.in',
  'net.in',
  'edu.in',
  'co.za',
  'org.za',
  'net.za',
  'gov.za',
  'edu.sg',
  'com.sg',
  'org.sg',
  'gov.sg',
];

export const breakDownHostUrl = (domain: string) => {
  // 移除 URL 前缀（如 www）
  domain = domain.toLowerCase().replace(/^www\./, '').trim();

  // 根据 '.' 分割域名
  const parts = domain.split('.');

  if (parts.length < 2) {
    return domain;
  }

  // 处理常见的复合 ccTLD
  const domainPart = parts.slice(parts.length - 2).join('.');

  if (ccTLDs.includes(domainPart)) {
    // 返回主域名 + 复合顶级域名，如 example.com.cn
    return parts.slice(parts.length - 3).join('.');
  }

  // 返回主域名 + 顶级域名，如 example.com
  return parts.slice(parts.length - 2).join('.');
};

const LOCAL_SLD_MAP = new Map([
  ['production', 'bitmart.com'],
  ['testing', 'bitmartgcp-test.com'],
  ['dev', 'bitmart-dev.com'],
]);
const LEGACY_TESTING_DOMAIN = 'bitmarttest.com';
const LEGACY_DEV_DOMAIN = 'bitmart-dev.com';

function checkRuntime() {
  const bitMartAppPattern = /BitMart/;
  const isAppRuntime = bitMartAppPattern.test(navigator?.userAgent);
  return isAppRuntime;
}

export const getAppDynamicHost = memoize(async ()=>{

  const isAppRuntime = checkRuntime();
  if (isAppRuntime) {
    const diff = 100;
    let totalTime = 0;

    // eslint-disable-next-line no-constant-condition
    while (true) {
      if (window.jsBridge && window.jsBridge.getDomainData) {
        break;
      } else {
        if (totalTime >= 1500) {
          console.log('no getDomainData');
          break;
        }
        await wait(diff);
        totalTime += diff;
      }
    }
  }
  return new Promise((resolve)=>{

    if (window.jsBridge && window.jsBridge.getDomainData) {
      console.log('getDomainData Start');
      window.jsBridge.getDomainData((res)=>{

        window.getAppDynamicHostResMsg = res;
        try {
          res = JSON.parse(res);
        } catch (_error) {
          res = res.substring(15,res.length-2);
          res = JSON.parse(res);
        }

        if (res.domainData && typeof res.domainData==='string') {
          res = JSON.parse(res.domainData);
        } else if (!res.domainData && !res?.domain_list) {
          return {};
        }

        if (res?.domain_list && typeof res?.domain_list==='string') {
          res = JSON.parse(res.domain_list);
        } else if (!res?.domain_list) {
          return {};
        }

        const {domain_list} = res;
        const domainsMap:any = {};
        const domainObj=domain_list.find((item) => {
          return Object.keys(item).find((key) => {
            const {host}=item[key];
            return process.env.DERIVATIVES_HOST?.includes(host)||process.env.MASTER_HOST?.includes(host)||process.env.AFFILIATE_HOST?.includes(host);
          });
        }) || domain_list?.[0];

        if (domainObj) {
          Object.keys(domainObj).forEach((key) => {
            const {host} = domainObj[key];
            if (host.length) {
              domainsMap[key]=host;
            }
          });
        }

        resolve(domainsMap);
      });
    }
  });
});

const isDerivatives = process.env.PROJECT_NAME === 'DERIVATIVES';
const PRODUCTION_SLD_LIST = ['bitmart.com', 'bitmart.cool', 'm-link.com', 'bm-top.com'];
const TESTING_SLD_LIST = ['bitmartgcp-test.com', 'bitmartgcp-test.cool', 'bmcngcp-test.com', 'bitmarttest.com'];
const DEV_SLD_LIST = ['bitmartgcp-dev.com'];
const ENV_SLD_LIST = (function(){
  if (process.env.domain_env === 'production') {
    return PRODUCTION_SLD_LIST;
  } else if (process.env.domain_env === 'testing') {
    return TESTING_SLD_LIST;
  } else {
    return DEV_SLD_LIST;
  }
})();

function checkAvailableHostname(hostname: string) {
  if (process.env.NODE_ENV === 'development') {
    return true;
  }
  switch (process.env.domain_env) {
    case 'production':
      return /(bitmart|m-link|bm-top).(com|cool)$/.test(hostname);
    case 'testing':
      return /(bitmartgcp-test|bmcngcp-test)\.(com|cool)$/.test(hostname);
    case 'dev':
      return /(bitmartgcp-dev)\.(com|cool)$/.test(hostname);
    default:
      return false;
  }
}

function getContractSld(sld: string) {
  switch (process.env.domain_env) {
    case 'testing':
      if (sld.startsWith('bitmartgcp-test') || sld.startsWith('bmcngcp-test')) {
        return LEGACY_TESTING_DOMAIN;
      }
      return sld;
    case 'dev':
      if (sld.startsWith('bitmartgcp-dev')) {
        return LEGACY_DEV_DOMAIN;
      }
      return sld;
    default:
      return sld;
  }
}

export const getCurHost = memoize((origin: string) => {
  let url: URL | undefined = undefined;
  let dynamicHost:any;
  let isInApp = false;

  // 当前域名是否为合约域名
  if (process.client) {
    // 检查 navigator 的 userAgent 属性是否包含 'BitMart' 字符串，以判断当前是否处于 BitMart 应用程序的运行环境中
    isInApp = checkRuntime();
    getAppDynamicHost().then((res) => {
      dynamicHost = res;
      console.log('getAppDynamicHost', res);
      window.getAppDynamicHostRes = res;
    });

    if (!isInApp && !checkAvailableHostname(location.hostname)) {
      if (isDerivatives) {
        location.href = location.href.replace(location.origin, process.env.DERIVATIVES_HOST as string);
      } else {
        location.href = location.href.replace(location.origin, process.env.MASTER_HOST as string);
      }
    }
  }
  try {
    if (isInApp) {
      if (isDerivatives) {
        url = new URL(process.env.DERIVATIVES_HOST as string);
      } else {
        url = new URL(process.env.MASTER_HOST as string);
      }
    } else {
      url = new URL(origin);
    }
  } catch (error) {
    console.log(111, error);
  }
  // console.log('domain_env', process.env.domain_env);
  const isLocal = process.env.NODE_ENV === 'development';
  // const isLocal = false;
  const isTest = process.env.domain_env === 'testing';
  const isProd = process.env.domain_env === 'production';

  let sld: string, isBeta: boolean, protocol = 'https:';
  if (url) {
    sld = breakDownHostUrl(url.host); // bitmartgcp-test.com / bitmartgcp-test.cool / bitmart.com / bitmart.cool
    isBeta = url.hostname.includes('beta.');
    protocol = url.protocol;
  } else {
    sld = 'bitmart.com';
    isBeta = false;
  }
  // const hostFixArr = sld.split('.');
  // tld = hostFixArr[hostFixArr.length - 1];

  if (isLocal) {
    sld = LOCAL_SLD_MAP.get(process.env.domain_env as string) as string;
    // if (isTest) {
    //   contractSld = LEGACY_TESTING_DOMAIN;
    // } else {
    //   contractSld = sld;
    // }
    protocol = 'https:';
  // } else if (isTest) {
    // contractSld = `bitmarttest.${tld}`;
  }
  const contractSld = getContractSld(sld);

  let siteOrigin;
  if (isBeta) {
    siteOrigin = `${protocol}//beta.${sld}`; // 主站的 地址
  } else {
    siteOrigin = `${protocol}//www.${sld}`; // 主站的 地址
  }

  let derivativesOrigin;
  if (isBeta) {
    derivativesOrigin = siteOrigin.replace('beta.', 'derivatives-beta.');
  } else {
    derivativesOrigin = siteOrigin.replace('www.', 'derivatives.');
  }
  const cookieDomain = `.${sld}`;

  let wssHostV2;
  let contractApiHostV2;
  let demoContractApiHost;
  let wsUrl;
  let wssDemoHost;
  if (isTest) {
    wssHostV2 = `wss://contract-ws-sandbox.${contractSld}`;
    contractApiHostV2 = `${protocol}//contract-sandbox.${contractSld}`;
    demoContractApiHost = `${protocol}//contract-simulation.${contractSld}`;
    wsUrl = `wss://ws-manager-compress-web.${sld}`;
    wssDemoHost = `wss://contract-ws-simulation.${contractSld}`;
  } else {
    wssHostV2 = `wss://contract-ws-v2.${contractSld}`;
    contractApiHostV2 = `${protocol}//contract-v2.${contractSld}`;
    demoContractApiHost = `${protocol}//contract-simulation.${contractSld}`;
    wsUrl = `wss://ws-manager-compress-web.${contractSld}`;
    wssDemoHost = `wss://contract-ws-simulation.${contractSld}`;
  }
  let imgCdnHost;
  let sentryHost = process.env.SENTRY_DSN || '';
  if (isProd) {
    imgCdnHost = `https://img.${sld}/static-file`;
    sentryHost = sentryHost.replace('bitmart.com', sld);
  } else {
    imgCdnHost = '/static-file';
  }

  const siteConfig = {
    origin: siteOrigin,
    derivativesOrigin, // https://derivatives.bitmart.com
    cookieDomain, // .bitmart.com
    isBeta,
    isDerivatives,
    contractApiHostV2,
    demoContractApiHost,
    wssHostV2,
    wssDemoHost,
    sld,
    wsUrl,
    isInApp,
    sentryHost,
    imgCdnHost,
    h5Origin: `https://h5.${sld}`,
    affiliateOrigin: isBeta ? `https://affiliate-beta.${sld}`: `https://affiliate.${sld}`,
  };
  if (process.env.domain_env !== 'production' && process.env.NODE_ENV === 'development') {
    console.log('getCurHost', siteConfig);
  }
  const ob = { ...siteConfig };
  Object.defineProperty(ob, 'wssHostV2', {
    get() {
        if (dynamicHost?.['contract-ws-sandbox.bitmarttest.com']) {
          return 'https://'+dynamicHost?.['contract-ws-sandbox.bitmart.com'];
        }else if (dynamicHost?.['contract-ws-v2.bitmart.com']) {
          return 'https://'+dynamicHost?.['contract-ws-v2.bitmart.com'];
        }else{
          return siteConfig.wssHostV2;
        }
    },
  });
  Object.defineProperty(ob, 'wssDemoHost', {
    get() {
      // console.log('in wssHost',dynamicHost)
      if (dynamicHost?.['contract-ws-simulation.bitmarttest.com']) {
        return 'wss://'+dynamicHost?.['contract-ws-simulation.bitmarttest.com'];
      }else if (dynamicHost?.['wss://contract-ws-simulation.bitmart.com']) {
        return 'wss://'+dynamicHost?.['contract-ws-simulation.bitmart.com'];
      }else{
        return siteConfig.wssDemoHost;
      }
    },
  });
  Object.defineProperty(ob, 'wsUrl', {
    get() {
      // console.log('in wsUrl',dynamicHost)
        if (dynamicHost?.['ws-manager-compress.bitmartgcp-test.com']) {
          return 'wss://'+dynamicHost?.['ws-manager-compress.bitmartgcp-test.com'];
        }else if (dynamicHost?.['ws-manager-compress-web.bitmart.com']) {
          return 'wss://'+dynamicHost?.['ws-manager-compress-web.bitmart.com'];
        }else{
          return siteConfig.wsUrl;
        }
    },
  });
  Object.defineProperty(ob, 'contractApiHostV2', {
    get() {
      // console.log('in contractApiHostV2',dynamicHost)
        if (dynamicHost?.['contract-sandbox.bitmarttest.com']) {
          return 'https://'+dynamicHost?.['contract-sandbox.bitmarttest.com'];
        }else if (dynamicHost?.['contract-v2.bitmart.com']) {
          return 'https://'+dynamicHost?.['contract-v2.bitmart.com'];
        }else{
          return siteConfig.contractApiHostV2;
        }
    },
  });
  Object.defineProperty(ob, 'demoContractApiHost', {
    get() {
      if (dynamicHost?.['contract-simulation.bitmarttest.com']) {
        return 'https://'+dynamicHost?.['contract-simulation.bitmarttest.com'];
      }else{
        return siteConfig.demoContractApiHost;
      }
    },
  });
  Object.defineProperty(ob, 'sentryHost', {
    get() {
      // console.log('in sentryHost',dynamicHost)
        if (dynamicHost?.['sentryingest.bitmart.com']) {
          return siteConfig.sentryHost?.replace('sentryingest.bitmart.com',dynamicHost?.['sentryingest.bitmart.com']);
        }else{
          return siteConfig.sentryHost;
        }
    },
  });
  Object.defineProperty(ob, 'imgCdnHost', {
    get() {
      // console.log('in sentryHost',dynamicHost)
        if (dynamicHost?.['img.bitmart.com']) {
          return siteConfig.imgCdnHost?.replace('img.bitmart.com', dynamicHost?.['img.bitmart.com']);
        } else {
          return siteConfig.imgCdnHost;
        }
    },
  });

  return ob;
});

const pathRegex = /^(\/[^?#\s]+(\?[^#\s]*)?)?$/;

export const convertToRelativeUrl = (targetUrl: string, sld: string) => {
  // 定义一个正则表达式来检查 targetUrl 是否为 URL 路径
  if (pathRegex.test(targetUrl) || !targetUrl) {
    return targetUrl;
  }
  const matchSld = ENV_SLD_LIST.find(sld => targetUrl.includes(sld));
  if (matchSld) {
    return targetUrl.replace(matchSld, sld);
  } else {
    return targetUrl;
  }
};
